import { useMutation } from '@apollo/client';
import { AttachmentItemHolder } from '@nile/banyan/src/Components/Editors/RTEditor';
import {
  AccessControl,
  AppContext,
  BackgroundIcon,
  DateTimeFormatter,
  generateString,
  getTranslatedText,
  AttachmentPreviewer,
  getDownloadURL,
  ALL_MODULES,
  useUserMSPCheckHook
} from '@nile/components';
import { SPLIT_TICKET } from '@nile/graphql/src';
import envConfig from '@nile/home/src/Config/env';
import SplitSheet from '@nile/mangifera/src/Components/Sheets/SplitSheet';
import { Receiver, Sender } from '@nile/mangifera/src/Pebbles/chat';
import {
  Popover,
  Link,
  Modal,
  TextContainer,
  Avatar,
  TextStyle
} from '@superops/ui-design-system';
import { DotsVertical } from '@styled-icons/boxicons-regular';
import dompurify from 'dompurify';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import styled from 'styled-components';
import { EditNoteContainer } from '@nile/mangifera/src/Containers/ChatContainers';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { ItemHolders, AttachmentsContainer } from './ConversationBubble';
import { dompurifier } from '@nile/components/src/Utils/helpers';

const StyledNote = styled.div`
  background: var(--sops-ticket-public-note-bg);
  border-radius: 20px 20px;
  width: 80%;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.102874);
  padding: 10px 15px 15px 15px;
  border: ${(props) => props.editState && '1px solid black'};
  opacity: ${(props) => props.editing && '0.5'}
  .action > div:first-child {
    cursor: pointer;
  }
  .message-content {
    margin-top: 10px;
    word-break: break-word;
  }
  .ap {
    background-image: url(${envConfig.imageRoot}emojis.webp);
  }
  .time {
    max-width: 35%;
  }
  .hover-hand {
    cursor: zoom-in;
  }
  .mw100{
    max-width: 100%;
  }

/* The Modal (background) */
.modal {
  display: none; /* Hidden by default */
  position: fixed; /* Stay in place */
  z-index: 10; /* Sit on top */
  // padding-top: 100px; /* Location of the box */
  left: 0;
  top: 0;
  width: 100%; /* Full width */
  height: 100%; /* Full height */
  overflow: auto; /* Enable scroll if needed */
  background-color: rgb(0,0,0); /* Fallback color */
  background-color: rgba(0,0,0,0.9); /* Black w/ opacity */
}

/* Modal Content (image) */
.modal-content {
  margin: auto;
  display: block;
  // width: 100%;
  max-width: 90vw;
}

@-webkit-keyframes zoom {
  from {-webkit-transform:scale(0)}
  to {-webkit-transform:scale(1)}
}

@keyframes zoom {
  from {transform:scale(0)}
  to {transform:scale(1)}
}

/* The Close Button */
.close {
  position: absolute;
  top: 15px;
  right: 35px;
  color: #f1f1f1;
  font-size: 40px;
  font-weight: bold;
  transition: 0.3s;
}

.close:hover,
.close:focus {
  color: #bbb;
  text-decoration: none;
  cursor: pointer;
}

/* 100% Image Width on Smaller Screens */
@media only screen and (max-width: 700px){
  .modal-content {
    width: 100%;
  }

`;

const OptionItems = styled.div`
  display: flex;
  gap: 6px;
  padding: 16px 12px 0;
  cursor: pointer;
`;

const PublicNoteLabel = styled.div`
  background: #c8e6c5;
  border-radius: 12px;
  height: 25px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  padding: 5px 8px;
  max-width: 115px;

  .title {
    max-width: calc(100% - 17px);
  }
`;

const NoteTitleWithName = styled.div`
  max-width: calc(100% - 130px);

  .name {
    max-width: calc(100% - 125px);
  }
`;

function NoteBubble(props) {
  const { message, intl, flags, showPrivacyType } = props;
  const [popoverActive, setPopoverActive] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [copyFields, setCopyFields] = useState([]);
  const [randomId, setRandomId] = useState(generateString(5) || 'abcde');
  const appContext = useContext(AppContext);
  const { user, company } = appContext;
  const [attachmentsList, setAttachments] = useState([]);
  const [showAttachmentPreview, setShowAttachmentPreview] = useState({
    show: false,
    index: 0
  });

  const [editState, setEditState] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleteModalActive, setDeleteModalActive] = useState(false);

  useEffect(() => {
    if (props.editNoteId === message?.data?.noteId && !props.editNoteLoading) {
      setEditState(true);
    }

    if (props.editNoteId === message?.data?.noteId && props.editNoteLoading) {
      setEditing(true);
      setEditState(false);
    }

    if (!props.editNoteId) {
      setEditState(false);
      setEditing(false);
    }
  }, [props.editNoteLoading, props.editNoteId]);

  const [splitTickets] = useMutation(SPLIT_TICKET, {
    onCompleted(res) {
      console.log(res);
    },
    onError(res) {
      console.log(res);
    }
  });

  const getAttachmentURL = (message, item) => {
    const module = ALL_MODULES.ticketNoteAttachment;
    const details = {
      fileName: item?.fileName,
      noteId: message?.id,
      clientId: message?.clientId,
      ticketId: message?.ticketId
    };
    const url = getDownloadURL({ module, details });
    return url;
  };

  useEffect(() => {
    let attachments = [];
    if (message && message.attachments && message.attachments.length)
      attachments = message.attachments.map((item) => {
        return {
          name: item?.fileName,
          type: item?.fileName?.substr(
            item?.fileName.lastIndexOf('.') + 1,
            item?.fileName.length
          ),
          url: props?.getAttachmentURL
            ? props?.getAttachmentURL(message, item)
            : getAttachmentURL(message, item)
        };
      });
    setAttachments(attachments);
  }, [message.attachments]);

  const togglePopoverActive = useCallback(() => {
    setPopoverActive((popoverActive) => !popoverActive);
    setTicketSubject('');
  });

  const handleEscPress = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (e?.keyCode === 27 || e?.which === 27 || e?.key === 'Escape') {
      if (
        window.getComputedStyle(document.querySelector(`.modal-inline-images`))
          .display === 'flex'
      ) {
        document.querySelector(`.modal-inline-images`).style.display = 'none';
      }
      window.removeEventListener('keyup', handleEscPress);
    }
  };

  const imageDownloader = (e) => {
    // window.open(e.target.currentSrc, '_blank').focus();
    const modal = document.querySelector(`.modal-inline-images`);

    const modalImg = document.querySelector(`.modal-content-inline-images`);

    modal.style.display = 'flex';
    modalImg.src = e.target.currentSrc;
    window.addEventListener('keyup', handleEscPress);
  };

  useEffect(() => {
    if (
      document?.querySelector(`.${randomId}`)?.querySelectorAll('img')?.length
    ) {
      document
        ?.querySelector(`.${randomId}`)
        ?.querySelectorAll('img')
        ?.forEach((img) => {
          img.classList.add('hover-hand', 'mw100');
          img.addEventListener('click', imageDownloader);
        });
      return () => {
        document
          ?.querySelector(`.${randomId}`)
          ?.querySelectorAll('img')
          .forEach((img) => {
            img.classList.remove('hover-hand');
            img.removeEventListener('click', imageDownloader);
          });
      };
    }
  }, []);

  const activator = (
    <div onClick={togglePopoverActive} className="ptr">
      <SVGIcon name="Menu" size="sm" />
    </div>
  );

  const splitTicket = (subject, copyFields) => {
    splitTickets({
      variables: {
        ticketId: message.ticketId,
        noteId: message.id,
        subject,
        copyFields
      }
    }).then((res) => {
      message.callback && message.callback(res);
    });
  };
  const Tags = {
    sender: Sender,
    receiver: Receiver
  };
  const userType =
    message.user?.userId === user?.userId ? 'sender' : 'receiver';
  const Tag = Tags[userType];

  const getUserName = () => {
    if (message?.user?.firstName) {
      return `${message.user.firstName} ${message.user?.lastName || ''}`;
    }
    return getTranslatedText(intl, 'ticket.details.note.system');
  };

  const getAvatarInitials = () => {
    if (message?.user?.firstName) {
      return `${message.user.firstName[0]}${message.user?.lastName?.[0] || ''}`;
    }
  };

  /**
   * @description
   * Closing attachment previewer
   */
  const closePreviwer = () => {
    setShowAttachmentPreview({
      ...showAttachmentPreview,
      show: false
    });
  };

  /**
   * @description
   * opening attachment previewer, if selected from a specific index then opening from the selected index.
   * @param {object} attachment
   * @param {number} index
   */

  function openAttachmentPreview(index) {
    setShowAttachmentPreview({
      ...showAttachmentPreview,
      show: true,
      index
    });
  }

  // TODO: ChatFeedContainer gets unmounted while editing note
  // Temporary fix by passing props from Ticket details page

  const handleEditNote = (msg) => {
    props.setEditNoteId(msg.data.noteId);
    setPopoverActive(false);
  };

  const handleDeleteNote = () => {
    props.deleteNote({
      variables: {
        input: {
          noteId: message?.data?.noteId
        }
      }
    });
  };

  const isMSP = useUserMSPCheckHook();

  const PrivacyType = message?.privacyType || message?.data?.privacyType;
  const isPublic = isMSP && showPrivacyType ? PrivacyType === 'PUBLIC' : false;
  const htmlString = dompurifier(message.message);
  const htmlToDisplay = htmlString.replaceAll('<img', "<img loading='lazy'");

  return (
    <Tag className="fx bubbleContainer">
      <div className="avatar">
        <Avatar
          size="small"
          initials={getAvatarInitials()}
          name={getUserName()}
          source={
            !message?.user?.firstName
              ? `${envConfig.imageRoot}svg/notification.svg`
              : ''
          }
        />
      </div>
      <StyledNote
        className={`bubble ${isPublic ? 'public-note' : ''}`}
        editState={editState}
        editing={editing}
      >
        <div className="fx actr">
          <NoteTitleWithName className="fx2 fx actr f-12">
            {!isPublic && (
              <div className="mr8">
                <SVGIcon name="Notes" size="sm" />
              </div>
            )}
            {isPublic ? (
              <>
                <PublicNoteLabel
                  className="fx actr g5 uppercase"
                  title={getTranslatedText(
                    intl,
                    'common.attribute.note.public'
                  )}
                >
                  <SVGIcon name="Notes" size="xs" />
                  <span className="ellipsis title">
                    <TextStyle variation="subdued f-12">
                      <FormattedMessage id="common.attribute.note.public" />
                    </TextStyle>
                  </span>
                  {/* <FormattedMessage id="ticket.details.chat.publicNote" /> */}
                </PublicNoteLabel>
                <span className="ml-5">
                  <TextStyle variation="subdued f-12">
                    <FormattedMessage id="common.keyword.by" />
                  </TextStyle>
                </span>
                {/* <FormattedMessage id="ticket.details.chat.by" /> */}
              </>
            ) : (
              <TextStyle variation="subdued f-12">
                <FormattedMessage id="ticket.details.chat.noteby" />
              </TextStyle>
            )}{' '}
            &nbsp;
            <div className="name ellipsis" title={getUserName()}>
              <TextStyle variation="strong">{getUserName()}</TextStyle>
            </div>
          </NoteTitleWithName>
          <div className="fx jend f-12 time">
            <TextStyle
              variation="subdued"
              title={<DateTimeFormatter type="dateTime" date={message?.time} />}
              // title={dayjs(message.time).format(
              //   `${company?.dateFormat ||
              //     'MMM DD YYYY'}, ${company?.timeFormat || 'hh:mm A'}`
              // )}
            >
              <DateTimeFormatter type="dateTime" date={message?.time} />
              {/* {dayjs(message.time).format(
                `${company?.dateFormat ||
                  'MMM DD YYYY'}, ${company?.timeFormat || 'hh:mm A'}`
              )} */}
            </TextStyle>
          </div>

          {!props.disableOptions &&
            (props?.allowEditDeleteNotes ? (
              (user?.userId === message?.user?.userId ||
                !props.disableSpit) && (
                <div className="flex action center pl5">
                  <Popover
                    active={popoverActive}
                    activator={activator}
                    onClose={() => {
                      togglePopoverActive();
                    }}
                  >
                    {user?.userId === message?.user?.userId && (
                      <>
                        <OptionItems onClick={() => handleEditNote(message)}>
                          <SVGIcon name="Edit" />
                          <FormattedMessage id="common.edit" />
                        </OptionItems>
                        <Modal
                          activator={
                            <OptionItems
                              onClick={() => setDeleteModalActive(true)}
                            >
                              <SVGIcon name="Delete" />
                              <FormattedMessage id="common.delete" />
                            </OptionItems>
                          }
                          open={deleteModalActive}
                          onClose={() => setDeleteModalActive(false)}
                          variant="confirmation"
                          title={getTranslatedText(
                            intl,
                            'ticket.note.delete.confirmation.title'
                          )}
                          primaryAction={{
                            content: getTranslatedText(intl, 'common.delete'),
                            onAction: handleDeleteNote,
                            destructive: true
                          }}
                          secondaryActions={[
                            {
                              content: getTranslatedText(intl, 'common.cancel'),
                              onAction: () => setDeleteModalActive(false)
                            }
                          ]}
                        >
                          <Modal.Section>
                            <TextContainer>
                              <FormattedMessage id="ticket.note.delete.confirmation.message" />
                            </TextContainer>
                          </Modal.Section>
                        </Modal>
                      </>
                    )}
                    {!props?.disableSpit && (
                      <SplitSheet splitTicket={splitTicket} />
                    )}
                  </Popover>
                </div>
              )
            ) : (
              <AccessControl
                roleToCheck="splitTicket.allow"
                render={
                  <div className="flex action center pl5">
                    <Popover
                      active={popoverActive}
                      activator={activator}
                      onClose={() => {
                        togglePopoverActive();
                      }}
                    >
                      <>
                        <AccessControl
                          roleToCheck="ticketingAddNotes.allow"
                          render={
                            <>
                              {user?.userId === message?.user?.userId && (
                                <>
                                  <OptionItems
                                    onClick={() => handleEditNote(message)}
                                  >
                                    <SVGIcon name="Edit" />
                                    <FormattedMessage id="common.edit" />
                                  </OptionItems>
                                  <Modal
                                    activator={
                                      <OptionItems
                                        onClick={() =>
                                          setDeleteModalActive(true)
                                        }
                                      >
                                        <SVGIcon name="Delete" />
                                        <FormattedMessage id="common.delete" />
                                      </OptionItems>
                                    }
                                    open={deleteModalActive}
                                    onClose={() => setDeleteModalActive(false)}
                                    variant="confirmation"
                                    title={getTranslatedText(
                                      intl,
                                      'ticket.note.delete.confirmation.title'
                                    )}
                                    primaryAction={{
                                      content: getTranslatedText(
                                        intl,
                                        'common.delete'
                                      ),
                                      onAction: handleDeleteNote,
                                      destructive: true
                                    }}
                                    secondaryActions={[
                                      {
                                        content: getTranslatedText(
                                          intl,
                                          'common.cancel'
                                        ),
                                        onAction: () =>
                                          setDeleteModalActive(false)
                                      }
                                    ]}
                                  >
                                    <Modal.Section>
                                      <TextContainer>
                                        <FormattedMessage id="ticket.note.delete.confirmation.message" />
                                      </TextContainer>
                                    </Modal.Section>
                                  </Modal>
                                </>
                              )}
                            </>
                          }
                        />
                        <SplitSheet splitTicket={splitTicket} />
                      </>
                    </Popover>
                  </div>
                }
              />
            ))}
        </div>
        {editState ? (
          <EditNoteContainer
            template={message.message}
            attachmentUploadUrl={`${envConfig.fileRoot}/upload`}
            allowMentions
            metadata={{ module: 'TICKET_NOTE' }}
            module={props?.module || 'TICKET_NOTIFICATION_TEMPLATE'}
            details={props?.details}
            saveState
            setEditState={props.setEditState}
            uploadParams={props.uploadParams}
            message={message}
            editNote={props.editNote}
            setEditNoteId={props.setEditNoteId}
            editNoteLoading={props.editNoteLoading}
            disabled={
              props.editNoteId === message?.data?.noteId &&
              props.editNoteLoading
            }
            onCancel={() => {
              props.setEditNoteId(null);
            }}
          />
        ) : (
          <>
            <p
              className={`message-content ${randomId}`}
              dangerouslySetInnerHTML={{
                __html: htmlToDisplay
              }}
            ></p>
            <ItemHolders className="mt-10">
              {message?.attachments?.map((item, index) => {
                return (
                  <AttachmentsContainer>
                    <Link
                      onClick={() => openAttachmentPreview(index)}
                      removeUnderline
                    >
                      {item?.originalFileName}
                    </Link>
                  </AttachmentsContainer>
                );
              })}
            </ItemHolders>
          </>
        )}
      </StyledNote>
      {showAttachmentPreview.show && (
        <AttachmentPreviewer
          showPreviewer={showAttachmentPreview.show}
          closePreviwer={closePreviwer}
          list={attachmentsList}
          selectedIndex={showAttachmentPreview.index}
        />
      )}
    </Tag>
  );
}

const NoteBubbleWrapper = (props) => {
  const [editState, setEditState] = useState(false);

  return (
    <>
      <NoteBubble
        {...props}
        editState={editState}
        setEditState={setEditState}
      />
    </>
  );
};

export default injectIntl(withLDConsumer()(NoteBubbleWrapper));
